import { useQuery } from '@tanstack/react-query'
import { Filter } from 'lucide-react'
import { Bar, BarChart, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { convictionApi } from '../api/client'
import { ChartCard } from './ChartCard'
import './LifecycleFunnel.css'

interface LifecycleFunnelProps {
  accountId: number
}

// Main pipeline stages in order. Any state key present in the response but
// not listed here (a schema surprise) falls through to the "other" side chip
// rather than being silently dropped or crashing the render.
const FUNNEL_STATES = ['discovered', 'incubating', 'ready_to_enter', 'entered', 'held'] as const
const SIDE_STATES = ['rejected', 'failed', 'benched', 'exited'] as const

const STATE_LABEL: Record<string, string> = {
  discovered: 'Discovered',
  incubating: 'Incubating',
  ready_to_enter: 'Ready',
  entered: 'Entered',
  held: 'Held',
  rejected: 'Rejected',
  failed: 'Failed',
  benched: 'Benched',
  exited: 'Exited',
}

export function LifecycleFunnel({ accountId }: LifecycleFunnelProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['conviction-status', accountId],
    queryFn: () => convictionApi.status(accountId),
    retry: 1,
    refetchInterval: 30000,
  })

  const byState = data?.candidates_by_state ?? {}
  const funnelData = FUNNEL_STATES.map((state) => ({
    state,
    label: STATE_LABEL[state] ?? state,
    count: byState[state] ?? 0,
  }))
  const sideChips = SIDE_STATES.map((state) => ({ state, label: STATE_LABEL[state] ?? state, count: byState[state] ?? 0 }))
  const recognized = new Set<string>([...FUNNEL_STATES, ...SIDE_STATES])
  const otherCount = Object.entries(byState).reduce(
    (sum, [key, value]) => (recognized.has(key) ? sum : sum + (value ?? 0)),
    0
  )

  const hasAnyData = data !== undefined

  return (
    <ChartCard
      className="lifecycle-funnel"
      title={
        <>
          <Filter size={18} className="icon" aria-hidden="true" />
          Pick lifecycle funnel
        </>
      }
      isLoading={isLoading}
      isError={isError}
      errorText="funnel unavailable"
      isEmpty={!hasAnyData}
      emptyText="lifecycle data unavailable"
      emptyIcon={<Filter size={28} aria-hidden="true" />}
    >
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={funnelData} layout="vertical" margin={{ left: 8, right: 24 }}>
          <XAxis type="number" stroke="var(--fg-subtle)" fontSize={11} allowDecimals={false} />
          <YAxis type="category" dataKey="label" stroke="var(--fg-subtle)" fontSize={11} width={80} />
          <Tooltip />
          <Bar dataKey="count" fill="var(--series-1)" radius={[0, 3, 3, 0]}>
            <LabelList dataKey="count" position="right" fill="var(--fg-muted)" fontSize={11} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div className="lifecycle-funnel-side">
        <span className="caption lifecycle-funnel-side-label">off-pipeline / terminal</span>
        <div className="lifecycle-funnel-chips">
          {sideChips.map((chip) => (
            <span key={chip.state} className="lifecycle-funnel-chip">
              {chip.label} <span className="num">{chip.count}</span>
            </span>
          ))}
          {otherCount > 0 && (
            <span className="lifecycle-funnel-chip">
              Other <span className="num">{otherCount}</span>
            </span>
          )}
        </div>
      </div>
    </ChartCard>
  )
}
